import { useEffect, useMemo, useState } from "react";
import * as LucideIcons from "lucide-react";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import UserManualOperate from "../../../assets/aftersalesservice_icons/user_manual_operate_icon.svg";
import FallbackImg from "../../../components/FallbackImg";
import { useLanguage } from "../LanguageContext";
import ApiService from "../../../components/Api_services";

// Mix a hex color towards white so it stays visible on top of that same
// color used solid as the card background (alpha-blending a color with
// itself, e.g. "#bb4d0080", just reproduces the same color with no contrast)
// const lightenColor = (hex, amount = 0.5) => {
//   if (!hex) return null;

//   const clean = hex.replace("#", "");
//   const normalized =
//     clean.length === 3
//       ? clean
//           .split("")
//           .map((c) => c + c)
//           .join("")
//       : clean;
//   const value = parseInt(normalized, 16);
//   if (Number.isNaN(value) || normalized.length !== 6) return hex;

//   const r = (value >> 16) & 255;
//   const g = (value >> 8) & 255;
//   const b = value & 255;
//   const lighten = (channel) => Math.round(channel + (255 - channel) * amount);

//   return `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
// };

// Render a lucide-react icon by name (e.g. "Info", "CircleCheck"), as returned by the API
const SectionIcon = ({ name, color, size = 20 }) => {
  const IconComp = name && LucideIcons[name];
  return IconComp ? (
    <IconComp
      color={color || undefined}
      size={size}
      style={{ flexShrink: 0 }}
    />
  ) : null;
};

const UserManualOperating = () => {
  const {
    trans,
    lang,
    countryCode: country,
    itemId1,
    itemId2,
    warrantyLoaded,
  } = useLanguage();
  const item_id = [itemId1, itemId2].filter(Boolean).map(Number);
  const [userManual, setUserManual] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const sections = useMemo(() => {
    const item = (userManual || [])[0];
    return (item?.sections || [])
      .slice()
      .sort((a, b) => (Number(a.seq_no) || 0) - (Number(b.seq_no) || 0));
  }, [userManual]);

  useEffect(() => {
    const fetchUserManual = async () => {
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;
      setLoaded(false);

      try {
        const response = await ApiService.getUserManual(
          item_id,
          apiLang,
          3,
          country,
        );
        if (response && response.status === 200) {
          setUserManual(response.data);
        } else {
          setUserManual(null);
          console.error("Error fetching user manual:", response.message);
        }
      } catch (error) {
        setUserManual(null);
        console.error("Error fetching user manual:", error);
      } finally {
        setLoaded(true);
      }
    };

    // wait for LanguageContext to resolve the country for this serial
    if (!warrantyLoaded) return;
    fetchUserManual();
  }, [itemId1, itemId2, country, lang, warrantyLoaded]);

  // shown once the fetch has finished (success or error) with no sections
  const emptySections = loaded && sections.length === 0 && (
    <div className="as-user-manual-item" style={{ backgroundColor: "#ffffff" }}>
      <div className="as-no-results" style={{ display: "block" }}>
        <div>{trans.no_user_manual}</div>
      </div>
    </div>
  );

  return (
    <AfterSalesLayout>
      <div
        className="as-user-manual-operate-content"
        id="user_manual_operating_modal"
      >
        <div style={{ padding: "0 31.97px" }}>
          {/* Header */}
          <div className="as-user-manual-operate-container">
            <button
              type="button"
              className="as-back-btn"
              id="user_manual_operating_back"
              onClick={() => window.history.back()}
            >
              <FallbackImg src={BackBlue} alt="backblue" />
              {trans.back_manual}
            </button>
            <div className="as-user-manual-operate-main">
              <FallbackImg src={UserManualOperate} alt="usermanualoperate" />
              <div>
                <div className="as-user-manual-operate-main-title">
                  {trans.operate_instruct}
                </div>
                <div className="as-user-manual-operate-main-text">
                  {trans.operate_instruct_sub_2}
                </div>
              </div>
            </div>
          </div>

          {sections.map((section) => (
            <div
              className="as-user-manual-item"
              key={section.id}
              style={{
                backgroundColor: section.background_color
                  ? section.background_color
                  : "#ffffff",
                color: section.text_color ? section.text_color : "#000000",
              }}
            >
              <div className="as-user-manual-title">
                <div
                  className="as-user-manual-title-icon"
                  // style={{
                  //   backgroundColor: section.background_color
                  //     ? lightenColor(section.background_color)
                  //     : "#f0f0f0",
                  // }}
                >
                  <SectionIcon name={section.icon} color={section.text_color} />
                </div>
                <div>{section.description}</div>
              </div>
              {(section.children || []).map((child) => (
                <div
                  className="as-user-manual-box"
                  key={child.id}
                  style={{
                    backgroundColor: child.background_color
                      ? child.background_color
                      : "transparent",
                    color: child.text_color ? child.text_color : "#000000",
                  }}
                >
                  <SectionIcon name={child.icon} color={child.text_color} />
                  <div>{child.description}</div>
                </div>
              ))}
            </div>
          ))}
          {emptySections}
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default UserManualOperating;
